/**
 * "Thinking…" status shown while an agent reply is pending.
 *
 * The web indicator paints the word through a `background-clip: text` gradient
 * that sweeps left→right forever. Reproducing that here would mean rendering
 * the label as SVG text with a gradient fill and animating the gradient's
 * offset — `react-native-svg` can do it, but the offset is an SVG prop, so the
 * sweep would run on the JS thread, and the label would have to be measured
 * by hand to size the gradient. Both costs land on a component that is on
 * screen precisely while the app is busy receiving a reply.
 *
 * So the effect is split in two instead: the COLOUR becomes the tone the sweep
 * averages to (the brand colour blended 45% toward muted — the same
 * `color-mix` ratio the web stops use), and the MOTION moves into the trailing
 * ellipsis, whose three dots bounce in sequence. Word rotation and the
 * enter/exit tween are unchanged, so the status still reads the same.
 */

import { useEffect, useRef, useState, type FC } from "react";
import { Animated, StyleSheet, Text, View, processColor } from "react-native";

import { solidColor } from "../lib/gradient";
import { ringgId } from "../lib/testing";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";

interface TypingIndicatorProps {
  words?: string[];
}

interface TypingDotProps {
  /** Position in the ellipsis, which is also the dot's stagger slot. */
  index: number;
  color: string;
  fontFamily: string | undefined;
}

// Cycled while an agent reply is pending. Words are overridable via the
// `typingWords` config.
export const DEFAULT_TYPING_WORDS = ["Thinking", "Reasoning", "Working on it", "Almost there"];

const WORD_INTERVAL_MS = 2200;

/**
 * Enter/exit tween of a word swap. `Animated.timing` already defaults to
 * `Easing.inOut(Easing.ease)`, the curve the web tween runs on, so no easing is
 * spelled out anywhere below.
 */
const SWAP_MS = 250;
/** The 2px the word travels: enters from `+2`, leaves toward `-2`. */
const SWAP_OFFSET = 2;

/** `color-mix(in srgb, brand 45%, muted)` — the softened shimmer stop. */
const WORD_TINT = 0.45;

const DOT_COUNT = 3;
/** One full bounce round trip; every dot repeats on this period. */
const DOT_CYCLE_MS = 1200;
const DOT_STAGGER_MS = 160;
const DOT_RISE_MS = 300;
const DOT_LIFT = 2;
const DOT_DIM_OPACITY = 0.4;

const DOT_SLOTS = Array.from({ length: DOT_COUNT }, (_, index) => index);

/**
 * `color-mix(in srgb, color <amount>, base)`. `processColor` is RN's own CSS
 * colour parser (hex, `rgb()`, named), so no colour syntax is re-implemented
 * here — it hands back an ARGB int, and unparseable input keeps the base.
 */
const mixColors = (color: string, base: string, amount: number): string => {
  const from = processColor(color);
  const to = processColor(base);
  if (typeof from !== "number" || typeof to !== "number") return base;
  const channel = (shift: number): number => Math.round(((from >>> shift) & 0xff) * amount + ((to >>> shift) & 0xff) * (1 - amount));
  return `rgba(${channel(16)}, ${channel(8)}, ${channel(0)}, ${(channel(24) / 0xff).toFixed(3)})`;
};

/** One half of a word swap — fade and slide move together. */
const swapTo = (fade: Animated.Value, lift: Animated.Value, opacity: number, offset: number): Animated.CompositeAnimation =>
  Animated.parallel([
    Animated.timing(fade, { toValue: opacity, duration: SWAP_MS, useNativeDriver: true }),
    Animated.timing(lift, { toValue: offset, duration: SWAP_MS, useNativeDriver: true }),
  ]);

const TypingDot: FC<TypingDotProps> = ({ index, color, fontFamily }) => {
  const bounce = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // The leading delay staggers this dot behind its neighbours; the trailing
    // one pads every dot back to the same DOT_CYCLE_MS period, so the three
    // never drift out of phase however long the reply takes.
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(index * DOT_STAGGER_MS),
        Animated.timing(bounce, { toValue: 1, duration: DOT_RISE_MS, useNativeDriver: true }),
        Animated.timing(bounce, { toValue: 0, duration: DOT_RISE_MS, useNativeDriver: true }),
        Animated.delay(DOT_CYCLE_MS - index * DOT_STAGGER_MS - 2 * DOT_RISE_MS),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [bounce, index]);

  return (
    <Animated.Text
      style={[
        styles.text,
        {
          color,
          fontFamily,
          opacity: bounce.interpolate({ inputRange: [0, 1], outputRange: [DOT_DIM_OPACITY, 1] }),
          transform: [{ translateY: bounce.interpolate({ inputRange: [0, 1], outputRange: [0, -DOT_LIFT] }) }],
        },
      ]}
    >
      .
    </Animated.Text>
  );
};

const TypingIndicator: FC<TypingIndicatorProps> = ({ words }) => {
  const theme = useWidgetTheme();
  const list = words && words.length > 0 ? words : DEFAULT_TYPING_WORDS;
  const [index, setIndex] = useState(0);

  // Start of the enter pose — the first word animates in on mount, like
  // AnimatePresence with an initial animation.
  const fade = useRef(new Animated.Value(0)).current;
  const lift = useRef(new Animated.Value(SWAP_OFFSET)).current;

  useEffect(() => {
    const enter = swapTo(fade, lift, 1, 0);
    enter.start();
    return () => enter.stop();
  }, [fade, lift]);

  useEffect(() => {
    const count = list.length;
    if (count <= 1) return;

    let swap: Animated.CompositeAnimation | undefined;
    const id = setInterval(() => {
      swap = swapTo(fade, lift, 0, -SWAP_OFFSET);
      swap.start(({ finished }) => {
        if (!finished) return;
        setIndex((current) => (current + 1) % count);
        // The word leaves upward but the next one arrives from below, so the
        // enter pose is set before it fades back in — never tweened through.
        lift.setValue(SWAP_OFFSET);
        swap = swapTo(fade, lift, 1, 0);
        swap.start();
      });
    }, WORD_INTERVAL_MS);

    return () => {
      clearInterval(id);
      swap?.stop();
      // A stopped swap leaves the word mid-fade. Restoring the resting pose
      // means a word-list change mid-swap snaps to a readable word instead of
      // stranding a half-transparent one.
      fade.setValue(1);
      lift.setValue(0);
    };
  }, [fade, lift, list.length]);

  const word = list[index % list.length] ?? "";
  // A gradient primary contributes its first stop, which is what the web
  // shimmer opens on.
  const color = mixColors(solidColor(theme.primaryColor), solidColor(theme.mutedTextColor), WORD_TINT);
  const fontFamily = resolveFontFamily(theme.fontFamily);

  return (
    // RN has no `role="status"`; an accessible container with a live region is
    // how both platforms announce a changing status without reading each word.
    <View testID={ringgId("typing-indicator")} accessible accessibilityLiveRegion="polite" accessibilityLabel="Agent is thinking" style={styles.row}>
      <Animated.View testID={ringgId("typing-indicator-word")} style={[styles.word, { opacity: fade, transform: [{ translateY: lift }] }]}>
        <Text style={[styles.text, { color, fontFamily }]}>{word}</Text>
        {DOT_SLOTS.map((slot) => (
          <TypingDot key={slot} index={slot} color={color} fontFamily={fontFamily} />
        ))}
      </Animated.View>
    </View>
  );
};

export default TypingIndicator;

const styles = StyleSheet.create({
  /** `mr-auto` keeps the indicator hugging the left edge of the transcript. */
  row: { flexDirection: "row", alignItems: "center", alignSelf: "flex-start", paddingVertical: 8 },
  word: { flexDirection: "row", alignItems: "center" },
  text: { fontSize: 14, lineHeight: 20, fontWeight: "500" },
});
